<template>
  <section ref="rootRef" class="marketing-rail">
    <TG4Image
      :image="rail.image"
      :aspect="aspect"
      :alt="title"
      class="banner-image"
      :id="itemId(0)"
      tabindex="0"
      v-focus
      @up="handleUp"
      @down="handleDown"
      @left="handleLeftFallback"
      v-press="handlePress"
    />

    <div :class="['content', buttonOnLeft ? 'button-left' : 'button-right']">
      <div class="text-block" :style="textStyle">
        <h3 class="rail-title" v-if="title">{{ title }}</h3>
        <p class="rail-subtitle" v-if="subtitle">{{ subtitle }}</p>
      </div>

      <div v-if="showActionButton" class="action-button" :style="buttonStyle">
        {{ actionButtonText }}
      </div>
    </div>

    <QRCodeModal
      v-if="showQRCode"
      :url="rail.actionButtonUrl ?? ''"
      :title="title"
      @close="closeQRCode"
    />
  </section>
</template>

<script setup lang="ts">

import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import { navigationService } from 'vue-spatialnavigation';
import type {ImageAspect} from '@/components/TG4Image.vue';
import { RailResponse } from '@/types/cms';
import { Language } from '@/enums/language';
import { useStore } from '@/composables/useStore';
import { useRailNavigation } from '@/composables/useRailNavigation';
import { useCMSItemNavigation } from '@/composables/useCMSItemNavigation';
import TG4Image from '@/components/TG4Image.vue';
import QRCodeModal from '@/components/QRCodeModal.vue';

const props = defineProps<{
  rail: RailResponse;
  railIndex: number;
  railDidNavigateDown?: (railIndex: number) => void;
}>();

const { itemId, handleUp, handleDown, handleLeftFallback } = useRailNavigation(props);
const { openItem } = useCMSItemNavigation();

const instance = getCurrentInstance();
const router = instance?.proxy?.$router;
const route = computed(() => instance?.proxy?.$route);

const { useGetter } = useStore();
const currentLanguage = useGetter<string>('currentLanguage');

const isIrish = computed(() => currentLanguage.value === Language.Irish);

const localised = (en: string | null, ga: string | null) => {
  const value = isIrish.value ? ga : en;
  return value ?? en ?? '';
};

const title = computed(() => localised(props.rail.titleEn, props.rail.titleGa));
const subtitle = computed(() => localised(props.rail.subtitleEn, props.rail.subtitleGa));
const actionButtonText = computed(() =>
  localised(props.rail.actionButtonTextEn, props.rail.actionButtonTextGa)
);


const aspect = computed<ImageAspect>(() => {
  switch (props.rail.railAspect) {
    case '32:9':
      return 'banner';
    case '64:9':
      return 'banner_thin';
    default:
      return 'banner';
  }
});

const buttonOnLeft = computed(() => props.rail.actionButtonPosition === 'LEFT');

const item = computed(() => props.rail.items[0] ?? null);

const showActionButton = computed(() => Boolean(props.rail.actionButtonUrl) || item.value !== null);

const textStyle = computed(() => ({
  color: props.rail.textColor ?? undefined,
}));


const buttonStyle = computed(() => ({
  'background-color': props.rail.backgroundColor ?? undefined,
  color: props.rail.textColor ?? undefined,
}));


//Hash per rail Id
const qrHash = computed(() => `showQRCode-${props.rail.id}`);

const showQRCode = computed(() => route.value?.hash === `#${qrHash.value}` );

const handlePress = () => {
  if (props.rail.actionButtonUrl) {
    router?.push({ path: router.currentRoute.path, hash: qrHash.value });
    return;
  }

  if (item.value) openItem(item.value);
};

const closeQRCode = () => {
  router?.back();
};

//Refocus on rail once closing modal if Ids match
watch(() => route.value?.hash, (newHash, oldHash) => {
  if (newHash || oldHash !== `#${qrHash.value}`) return;

  setTimeout(() => {
    const element = navigationService.getFocusElementById(itemId(0));
    if (element) element.focus();
  }, 100);
});

const rootRef = ref<HTMLElement | null>(null);
const lazyLoad = instance?.proxy?.$lazyLoad;


const scanForLazyImages = () => {
  setTimeout(() => {
    if (rootRef.value) lazyLoad?.(rootRef.value);
  }, 200);
};

onMounted(scanForLazyImages);

</script>

<style scoped lang="scss">
.marketing-rail {
  position: relative;
  display: block;
}

.banner-image {
  outline: 0;
  box-sizing: border-box;
}

// Messy but focus on button when image focuses
.banner-image:focus + .content .action-button {
  border-color: currentColor;
}

.content {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  padding: 0 60px;
}

.button-left {
  flex-direction: row-reverse;
}

.button-right {
  flex-direction: row;
}

.text-block {
  flex: 1;
  min-width: 0;
  text-align: left;
  @include text-white;
}

.rail-title {
  margin: 0;
  @include text-rail-title;
}

.rail-subtitle {
  margin: 10px 0 0 0;
  @include text-rail-subtitle;
}

.action-button {
  flex-shrink: 0;
  margin: 0 40px;
  padding: 14px 40px;
  background-color: $color-pink;
  color: $color-off-white;
  @include text-button;
  border: $focus-border-width solid transparent;
  box-sizing: border-box;
}
</style>
